/**
 * connectionHealth — what a socket's traffic tells us, and what it doesn't.
 *
 * Three states, and the distinction between two of them is the one that matters.
 */

export type HealthStatus = 'healthy' | 'stale' | 'degraded';

export interface HealthMonitorOptions {
  staleAfterMs?: number;
  initialActivityAt?: number;
}

export interface HealthMonitor {
  markActivity(now?: number): void;
  markFailure(reason?: string): void;
  isHealthy(now?: number): boolean;
  getStatus(now?: number): HealthStatus;
  getLastActivityAt(): number;
  getFailureReason(): string | undefined;
  reset(now?: number): void;
}

const DEFAULTS = {
  staleAfterMs: 45_000
};

export function createHealthMonitor(
  options: HealthMonitorOptions = {}
): HealthMonitor {
  const staleAfterMs = options.staleAfterMs ?? DEFAULTS.staleAfterMs;
  let lastActivityAt = options.initialActivityAt ?? Date.now();
  let degraded = false;
  let failureReason: string | undefined;

  return {
    markActivity(now = Date.now()) {
      lastActivityAt = now;
      degraded = false;
      failureReason = undefined;
    },
    markFailure(reason?: string) {
      degraded = true;
      failureReason = reason;
    },
    isHealthy(now = Date.now()) {
      if (degraded) {
        return false;
      }
      return now - lastActivityAt < staleAfterMs;
    },
    getStatus(now = Date.now()): HealthStatus {
      if (degraded) {
        return 'degraded';
      }
      return now - lastActivityAt < staleAfterMs ? 'healthy' : 'stale';
    },
    getLastActivityAt() {
      return lastActivityAt;
    },
    getFailureReason() {
      return failureReason;
    },
    reset(now = Date.now()) {
      lastActivityAt = now;
      degraded = false;
      failureReason = undefined;
    }
  };
}
